import { useMemo } from 'react'
import { Card, CardHeader } from '../ui-kitNew/cardNew'
import { IconButton } from '../ui-kitNew/buttonNew'
import { Badge } from '../ui-kitNew/controlsNew'
import { Popover } from '../ui-kitNew/popoverNew'
import { Button } from '../ui-kitNew/buttonNew'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CopyIcon,
  FilterIcon,
  PencilIcon,
  SlidersIcon,
  TrashIcon,
} from '../ui-kitNew/iconsNew'
import { ChartRenderer } from '../chartsNew/chart-rendererNew'
import { KpiView } from '../chartsNew/kpi-viewNew'
import { aggregateRows, aggregateValue, metricFormat, metricLabel } from '../../lib/reportNew/aggregateNew'
import { applyFilterGroup, countActiveConditions, describeCondition, isConditionActive } from '../../lib/reportNew/filtersNew'

const SPAN_CLASS = { 1: 'lg:col-span-1', 2: 'lg:col-span-2', 3: 'lg:col-span-3' }

/**
 * Tarjeta de gráfico: aplica los filtros propios del widget, agrega los datos
 * y delega el dibujado. Expone las acciones de edición del cuadro de mando.
 */
export function WidgetCard({ widget, rows, onEdit, onDuplicate, onRemove, onMove, isFirst, isLast }) {
  const scopedRows = useMemo(() => applyFilterGroup(rows, widget.filters), [rows, widget.filters])

  const result = useMemo(() => {
    if (widget.chartType === 'kpi') {
      const value = aggregateValue(scopedRows, widget.metric)
      const breakdown = widget.dimension
        ? aggregateRows(scopedRows, { ...widget, breakdown: '', limit: 4, groupOthers: true }).data
        : []
      return { kpi: true, value, breakdown, format: metricFormat(widget.metric), label: metricLabel(widget.metric) }
    }
    return aggregateRows(scopedRows, widget)
  }, [scopedRows, widget])

  const ownFilters = countActiveConditions(widget.filters)

  return (
    <Card className={`flex flex-col ${SPAN_CLASS[widget.span] || SPAN_CLASS[1]}`}>
      <CardHeader
        title={widget.title}
        subtitle={widget.subtitle}
        actions={
          <>
            {ownFilters > 0 && (
              <Popover
                align="end"
                width={260}
                renderTrigger={({ toggle, props }) => (
                  <button
                    type="button"
                    onClick={toggle}
                    {...props}
                    aria-label="Ver filtros de este gráfico"
                    className="inline-flex"
                  >
                    <Badge tone="primary">
                      <FilterIcon size={11} />
                      {ownFilters}
                    </Badge>
                  </button>
                )}
              >
                <div className="flex flex-col gap-2">
                  <p className="text-[12px] font-semibold text-foreground">Filtros de este gráfico</p>
                  <ul className="flex flex-col gap-1">
                    {widget.filters.conditions.filter(isConditionActive).map((condition) => (
                      <li key={condition.id} className="text-[12.5px] leading-snug text-muted-foreground">
                        {describeCondition(condition)}
                      </li>
                    ))}
                  </ul>
                  <Button variant="ghost" size="sm" onClick={onEdit}>
                    <SlidersIcon size={14} />
                    Editar filtros
                  </Button>
                </div>
              </Popover>
            )}

            <IconButton label="Mover a la izquierda" disabled={isFirst} onClick={() => onMove(-1)}>
              <ChevronLeftIcon size={15} />
            </IconButton>
            <IconButton label="Mover a la derecha" disabled={isLast} onClick={() => onMove(1)}>
              <ChevronRightIcon size={15} />
            </IconButton>
            <IconButton label="Editar gráfico" onClick={onEdit}>
              <PencilIcon size={15} />
            </IconButton>
            <IconButton label="Duplicar gráfico" onClick={onDuplicate}>
              <CopyIcon size={15} />
            </IconButton>
            <IconButton label="Eliminar gráfico" onClick={onRemove} className="hover:text-destructive">
              <TrashIcon size={15} />
            </IconButton>
          </>
        }
      />

      <div className="flex-1 px-4 pt-1 pb-4">
        {result.kpi ? (
          <KpiView
            value={result.value}
            format={result.format}
            label={result.label}
            breakdown={result.breakdown}
          />
        ) : (
          <ChartRenderer widget={widget} result={result} />
        )}
      </div>
    </Card>
  )
}
